import { useEffect, useRef, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { addPartnerTeams, updateUserGroup } from "../../../services/PartnerService";
import { getUsersWhoCanBeAssignedAsReviewers } from "../../../services/UserService";
import { showMutationError } from "../../../utils/showMutationError";
import { isExpenseTagPrerequisiteError, prerequisiteMessageFor } from "../../../utils/prerequisiteErrors";
import PrerequisiteAlert from "../PrerequisiteAlert";

const TeamModal = ({ show, onHide, mode, team, partnerId, enabled = true }) => {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [reviewerSearch, setReviewerSearch] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [topLevelError, setTopLevelError] = useState(null);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setName(team?.name ?? "");
    setReviewers([]);
    setReviewerSearch("");
    setFieldErrors({});
    setTopLevelError(null);
  }, [show, team]);

  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;
    const firstInvalid = modalBodyRef.current?.querySelector(".is-invalid");
    firstInvalid?.focus?.();
  }, [fieldErrors]);

  const { data: reviewersData } = useQuery({
    queryKey: ["usersWhoCanBeAssignedAsReviewers", partnerId],
    queryFn: () => getUsersWhoCanBeAssignedAsReviewers(partnerId),
    enabled: !!partnerId && enabled && show && !isEdit,
    staleTime: 5 * 60 * 1000,
  });
  const eligibleReviewers = reviewersData?.data?.data ?? null;

  const invalidateAfterMutation = () => {
    queryClient.invalidateQueries({ queryKey: ["partnerTeams", partnerId] });
    queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
    queryClient.invalidateQueries({ queryKey: ["usersWhoCanBeAssignedAsReviewers", partnerId] });
  };

  const addMutation = useMutation({
    mutationFn: (payload) => addPartnerTeams(partnerId, payload),
    onSuccess: () => {
      toast.success("User group added successfully.");
      invalidateAfterMutation();
      onHide();
    },
    onError: (error) => {
      if (isExpenseTagPrerequisiteError(error)) {
        setTopLevelError(prerequisiteMessageFor(error, "Add at least one expense tag before creating a team."));
        queryClient.invalidateQueries({ queryKey: ["expenseTags", partnerId] });
        return;
      }
      showMutationError(error, {
        fallback: "Adding team(s) failed.",
        onFieldErrors: setFieldErrors,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ teamId, ...payload }) => updateUserGroup(partnerId, teamId, payload),
    onSuccess: () => {
      toast.success("Team updated successfully.");
      invalidateAfterMutation();
      onHide();
    },
    onError: (error) => {
      showMutationError(error, {
        fallback: "Updating team failed.",
        onFieldErrors: setFieldErrors,
      });
    },
  });

  const isSubmitting = addMutation.isPending || updateMutation.isPending;

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearReviewerRowErrors = (idx) => {
    setFieldErrors((prev) => {
      const prefix = `reviewerData.${idx}.`;
      const affected = Object.keys(prev).filter((key) => key.startsWith(prefix));
      if (affected.length === 0) return prev;
      const next = { ...prev };
      affected.forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || isSubmitting) return;

    setFieldErrors({});
    setTopLevelError(null);

    if (isEdit) {
      updateMutation.mutate({ teamId: team.id, name });
    } else {
      const reviewerData = reviewers.map((r) => ({
        reviewer_id: r.userId,
        user_transaction_limit_extension_max_amount: r.user_transaction_limit_extension_max_amount,
      }));
      addMutation.mutate({ name, reviewerData });
    }
  };

  const reviewerNames = eligibleReviewers?.map((u) => `${u.first_name} ${u.last_name}`) ?? [];
  const selectedNames = reviewers.map((r) => r.full_name);
  const reviewerLabel = selectedNames.length === 0
    ? "Select reviewers..."
    : `${selectedNames.length} selected`;
  const needle = reviewerSearch.trim().toLowerCase();
  const filteredNames = needle
    ? reviewerNames.filter((n) => n.toLowerCase().includes(needle))
    : reviewerNames;

  const toggleReviewer = (fullName) => {
    if (selectedNames.includes(fullName)) {
      const removedIdx = reviewers.findIndex((r) => r.full_name === fullName);
      setReviewers(reviewers.filter((r) => r.full_name !== fullName));
      if (removedIdx !== -1) clearReviewerRowErrors(removedIdx);
      return;
    }
    const matched = eligibleReviewers.find(
      (u) => `${u.first_name} ${u.last_name}` === fullName
    );
    if (!matched) return;
    setReviewers([...reviewers, {
      userId: matched.id,
      full_name: fullName,
      user_transaction_limit_extension_max_amount: "",
    }]);
  };

  const nameError = fieldErrors.name;

  return (
    <Modal show={show} onHide={onHide}>
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">{isEdit ? "Edit Group Name" : "Add User Group"}</h5>
          <button type="button" className="btn-close" onClick={onHide} />
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body" ref={modalBodyRef}>
            <div className="form-group mb-3">
              <label htmlFor="team-name-input">Name</label>
              <input
                id="team-name-input"
                type="text"
                className={`form-control${nameError ? " is-invalid" : ""}`}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) clearFieldError("name");
                }}
              />
              {nameError && <div className="invalid-feedback d-block">{nameError}</div>}
            </div>

            {!isEdit && eligibleReviewers && (
              <>
                <div className="mb-3">
                  <label className="form-label d-block">Reviewers</label>
                  <Dropdown
                    className="w-100"
                    autoClose="outside"
                    onToggle={(nextOpen) => { if (!nextOpen) setReviewerSearch(""); }}
                  >
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      size="sm"
                      className="d-inline-flex align-items-center w-100"
                    >
                      <span className="flex-grow-1 text-start">{reviewerLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 p-0">
                      <div className="px-3 py-2 border-bottom text-muted small">
                        Not seeing an accountant? For accountability, whoever sends a payment cannot also approve it. Accountants initiate wallet payments, so team reviewers are picked from other roles.
                      </div>
                      <div className="p-2 border-bottom">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search"
                          value={reviewerSearch}
                          onChange={(e) => setReviewerSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                        {reviewerNames.length === 0 ? (
                          <div className="px-3 py-2 text-muted small">No eligible reviewers.</div>
                        ) : filteredNames.length === 0 ? (
                          <div className="px-3 py-2 text-muted small">No reviewers match.</div>
                        ) : filteredNames.map((fullName) => {
                          const isSelected = selectedNames.includes(fullName);
                          return (
                            <Dropdown.Item
                              key={fullName}
                              active={isSelected}
                              onClick={() => toggleReviewer(fullName)}
                              className="d-flex align-items-center gap-2"
                            >
                              <i
                                className={`fa ${isSelected ? "fa-check-square" : "fa-square-o"}`}
                                aria-hidden="true"
                              />
                              <span>{fullName}</span>
                            </Dropdown.Item>
                          );
                        })}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {reviewers.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label d-block">
                      Max approval amount <span className="text-muted">(leave blank for unlimited)</span>
                    </label>
                    <div className="d-flex flex-column gap-2">
                      {reviewers.map((r, idx) => {
                        const amountKey = `reviewerData.${idx}.user_transaction_limit_extension_max_amount`;
                        const reviewerIdKey = `reviewerData.${idx}.reviewer_id`;
                        const amountError = fieldErrors[amountKey];
                        const reviewerIdError = fieldErrors[reviewerIdKey];
                        return (
                          <div
                            key={r.userId}
                            className="d-flex flex-column gap-1"
                          >
                            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                              <div className="fw-semibold flex-grow-1" style={{ overflowWrap: "anywhere" }}>
                                {r.full_name}
                              </div>
                              <div className="input-group input-group-sm" style={{ maxWidth: "200px" }}>
                                <span className="input-group-text">KES</span>
                                <input
                                  type="number"
                                  className={`form-control${amountError ? " is-invalid" : ""}`}
                                  placeholder="No limit"
                                  value={r.user_transaction_limit_extension_max_amount}
                                  onChange={(e) => {
                                    const updated = [...reviewers];
                                    updated[idx].user_transaction_limit_extension_max_amount = e.target.value;
                                    setReviewers(updated);
                                    if (amountError) clearFieldError(amountKey);
                                  }}
                                />
                              </div>
                            </div>
                            {reviewerIdError && (
                              <div className="text-danger small">{reviewerIdError}</div>
                            )}
                            {amountError && (
                              <div className="text-danger small">{amountError}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {topLevelError && (
              <PrerequisiteAlert message={topLevelError} />
            )}
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={!name.trim() || isSubmitting}
            >
              {isSubmitting ? <i className="fa fa-spinner fa-spin" /> : <>Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TeamModal;
